// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

/**
 * this is a component to show a title for a section with multip options like,
 * show border on the top or bottom of the text(title) and another option to show "view >" link with the url and you can pass the text of the link also
 *
 * <SectionTitle
 *      title="string"
 *      border: top | bottom | none
 *
 * </SectionTitle>
 */

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import FieldDefinition, { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import IconArrowRight from "@pace/XDL/Icons/IconArrowRight";
import { color, createLinkIconStyles, mergeWithCss } from "@pace/XDL/Utilities";
import React, { FC, useState } from "react";
import { css } from "styled-components";

interface SectionTitleProps {
    title: string;
    viewMoreLink?: string | LinkFieldValue;
    viewMoreText?: string;
    border?: "top" | "bottom" | "none";
    extendedStyles?: SectionTitleStyles;
    extraChild?: React.ReactNode;
    className?: string;
}

type Props = SectionTitleProps;

export interface SectionTitleStyles {
    wrapper?: InjectableCss;
    title?: TypographyProps;
    link?: LinkPresentationProps;
}

const sectionTitleStyles: SectionTitleStyles = {
    wrapper: {
        css: css`
            margin-bottom: 16px;
            width: 100%;
            ${({ border }: any) => {
                if (border === "bottom") {
                    return css`
                        border-bottom: 1px solid ${color("common.border")};
                        padding-bottom: 14px;
                    `;
                }

                if (border === "none") {
                    return css`
                        border: none;
                    `;
                }

                return css`
                    border-top: 1px solid ${color("common.border")};
                    padding-top: 14px;
                `;
            }}
        `,
    },
    title: {
        variant: "h4",
        forwardAs: "h2",
        css: css`
            display: inline-block;
            margin: 0;
        `,
    },
    link: {
        ...createLinkIconStyles(IconArrowRight),
    },
};

const SectionTitle: FC<Props> = ({
    title,
    viewMoreLink,
    viewMoreText,
    border,
    extendedStyles,
    extraChild = null,
    className = "",
}) => {
    const [styles] = useState(() => mergeWithCss(sectionTitleStyles, extendedStyles));
    const linkUrl =
        typeof viewMoreLink === "string" ? viewMoreLink : useGetLink(viewMoreLink ?? { type: "Page", value: "" }).url;
    return (
        <StyledWrapper {...styles.wrapper} {...{ border }} className={className}>
            <Typography {...styles.title}>{title}</Typography>

            {linkUrl && (
                <Link href={linkUrl} {...styles.link} aria-label={viewMoreText || translate("View More")}>
                    {viewMoreText || translate("View More")}
                </Link>
            )}
            {extraChild}
        </StyledWrapper>
    );
};

const enum sectionTitleFieldsEnum {
    title = "title",
    viewMoreText = "viewMoreText",
    viewMoreLink = "viewMoreLink",
    border = "border",
}

const SectionTitleFields: FieldDefinition[] = [
    {
        name: "title",
        editorTemplate: "TextField",
        displayName: "Section Title",
        defaultValue: "",
        fieldType: "Translatable",
    },
    {
        name: "viewMoreText",
        editorTemplate: "TextField",
        defaultValue: "",
        fieldType: "Translatable",
    },
    {
        name: "viewMoreLink",
        editorTemplate: "LinkField",
        defaultValue: {
            type: "Page",
            value: "",
        },
        fieldType: "General",
    },
    {
        name: "border",
        editorTemplate: "DropDownField",
        options: [
            {
                displayName: "Top",
                value: "top",
            },
            {
                displayName: "Bottom",
                value: "bottom",
            },
            {
                displayName: "None",
                value: "none",
            },
        ],
        defaultValue: "top",
        fieldType: "General",
    },
];

export default SectionTitle;
export { SectionTitleFields, sectionTitleFieldsEnum };

